import { useQuery } from '@apollo/react-hooks';
import { useAccount } from '@bnnvara/accounts';
import getConfig from 'next/config';
import { useEffect, useState } from 'react';

import { FetchSignature } from '../../api/queries/me/notifications.gql';
import connectToWebsocket, {
  WS_STATUS,
} from '../../helpers/connectToWebsocket';
import getUniqueNotifications from './getUniqueNotifications';

const useNotificationsWebsocket = ({ setNotificationsHistory }) => {
  const [latestMessage, setLatestMessage] = useState(null);
  const [websocketStatus, setWebsocketStatus] = useState(WS_STATUS.IDLE);
  const [websocketServerLocation, setWebsocketServerLocation] = useState();
  const { profile, token } = useAccount();

  const UID = profile && profile.UID;
  const {
    publicRuntimeConfig: {
      notifications: { websocketUrl },
    },
  } = getConfig();
  const { data } = useQuery(FetchSignature, {
    variables: {
      token,
      type: 'lambdaNotifications',
    },
    skip: !token,
  });

  /**
   * This useEffect sets the url for the websocket connection.
   */
  useEffect(() => {
    if (!UID || !websocketUrl || !data) return;
    const { signature } = data.me;

    if (signature) {
      setWebsocketServerLocation(
        `${websocketUrl}?userId=${UID}&hash=${signature}`
      );
    }
  }, [websocketUrl, UID, data]);

  /**
   * This useEffect facilitates the connection with the webSocket
   */
  useEffect(() => {
    if (!websocketServerLocation) return;

    /**
     * When a user is logged in,
     * an attempt is being made to connect with the websocket.
     */
    if (UID && websocketStatus === WS_STATUS.IDLE) {
      connectToWebsocket({
        serverLocation: websocketServerLocation,
        setStatus: setWebsocketStatus,
        setLatestMessage,
      });
      return;
    }

    let timer;

    /**
     * When we loose connection with the server,
     * an attempt to reconnect is being made every 5 seconds.
     */
    if (websocketStatus === WS_STATUS.CLOSED) {
      timer = setInterval(() => {
        connectToWebsocket({
          serverLocation: websocketServerLocation,
          setStatus: setWebsocketStatus,
          setLatestMessage,
        });
      }, 5000);
    }
    // eslint-disable-next-line
    return () => clearInterval(timer);
  }, [
    websocketStatus,
    UID,
    setLatestMessage,
    websocketUrl,
    websocketServerLocation,
  ]);

  /**
   * This useEffect includes the message received from the webSocket
   * in the notificationHistory.
   */
  useEffect(() => {
    if (typeof latestMessage === 'object' && latestMessage !== null) {
      setNotificationsHistory((prevState) =>
        getUniqueNotifications(latestMessage, prevState)
      );
    }
  }, [latestMessage, setNotificationsHistory]);

  return { websocketStatus, latestMessage };
};

export default useNotificationsWebsocket;
